/** Strukturierter LEXCORE-Nachweis (n8n → Portal, ausklappbar). */

export interface LexProofSource {
  norm_id: string;
  title?: string;
  fragment_text?: string;
  ris_url?: string;
  valid_from?: string;
}

/** LEXCORE norm_chain-Eintrag (Primary + Cross-Refs aus Snapshot). */
export interface LexProofNormChainEntry {
  norm_id: string;
  rank?: number;
  /** z. B. primary_match | cross_ref:<relation> */
  via?: string;
}

export interface LexProof {
  audit_id?: string;
  snapshot_version?: string;
  lex_stamp?: string;
  retrieved_at?: string;
  query_reason?: string;
  query_enriched?: boolean;
  lex_query?: string;
  lex_diag?: string;
  quality_gate?: string;
  sources?: LexProofSource[];
  /** Verwandte/Primary-Kette aus Snapshot-cross_refs (Portal: Related-Liste). */
  norm_chain?: LexProofNormChainEntry[];
}

const QUERY_REASON_LABELS: Record<string, string> = {
  plain: 'Ihre Frage wurde unverändert nachgeschlagen.',
  vague_plus_leitfragen_block:
    'Die Frage war unspezifisch — die Suche wurde mit dem Block „Rechtliche Leitfragen“ aus dem geöffneten Dokument angereichert.',
  vague_plus_first_leitfrage:
    'Die Frage war unspezifisch — die erste nummerierte Leitfrage aus dem Dokument diente als Suchanker.',
  vague_plus_doc_head:
    'Die Frage war unspezifisch — der Dokumentanfang wurde als Sachverhalt ergänzt.',
  vague_no_doc_dump:
    'Die Frage war unspezifisch — der Dokumentanfang wurde nicht in den Normenbestand gegeben.',
  snapshot_scope_skip:
    'Die Materie liegt außerhalb des aktuellen Normenpakets (derzeit Mietrecht) — es wurde nicht nachgeschlagen.',
  specific_plus_kurzfassung:
    'Konkrete Rechtsfrage — die Kurzfassung aus dem Dokument wurde als Sachverhalt ergänzt.',
  specific_only: 'Konkrete Rechtsfrage — ohne zusätzliche Dokument-Anreicherung.',
};

const LEX_DIAG_LABELS: Record<string, string> = {
  hard_quote: 'Belegter Wortlaut aus dem Normenbestand (ohne KI-Paraphrase).',
  single_norm_quote: 'Expliziter §-Anker — belegter Wortlaut aus dem Normenbestand.',
  multi_norm_quote: 'Mehrere explizite §-Anker — belegte Wortlaute je Norm.',
  sole_source_quote: 'Wortlaut-Frage — einzige belegte Quelle aus dem Normenbestand.',
  sources_listing_quote: 'Normen-Listing — belegte Wortlaute aus dem Normenbestand.',
  weak_match_sources_quote: 'Teilweise passende Normen — belegte Wortlaute aus dem Normenbestand.',
  weak_match: 'Kein belastbarer Normtreffer — ehrliche Absage statt freier KI-Antwort.',
  no_sources: 'Keine passenden Quellen für diese Anfrage.',
  http_error: 'Der Normenbestand war kurzzeitig nicht erreichbar.',
  replaced_no_lexcore_stamp: 'Antwort ohne gültigen Beleg verworfen.',
  replaced_contradiction_with_stamp: 'Widersprüchliche KI-Antwort durch ehrliche Absage ersetzt.',
  rejected_hallucinated_norm_id: 'Antwort verworfen — zitierte Norm lag nicht im belegten Bestand.',
  auto_appended_lex_stamp: 'Beleg nachträglich ergänzt (Sicherheit).',
  vague_no_match: 'Nichts Passendes gefunden. Bitte anders versuchen — keine belegte Norm.',
  no_sources_honest: 'Keine belegte Norm für diese Anfrage.',
  snapshot_scope_miss:
    'Die Materie liegt nicht im aktuellen Normenpaket (derzeit Mietrecht).',
  no_match_above_threshold: 'Nichts Passendes gefunden. Bitte anders versuchen — keine belegte Norm.',
  replaced_no_stamp_doc_aware: 'Keine einzelne Norm belegt — Kandidaten ohne Prüfstempel.',
  replaced_contradiction_doc_aware: 'Widersprüchliche Antwort ersetzt — keine belegte Norm.',
  allrounder_fallback_doc_aware: 'Allgemeine Orientierung ohne belegten Normenstand.',
  allrounder_fallback: 'Allgemeine Orientierung ohne belegten Normenstand.',
  kuendigung_vs_mangel: 'Themen-Mismatch — keine belegte Kündigungs-Norm zitiert.',
};

export function parseLexProof(raw: unknown): LexProof | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const sources = Array.isArray(o.sources)
    ? o.sources
        .filter((s): s is Record<string, unknown> => Boolean(s && typeof s === 'object'))
        .map((s) => ({
          norm_id: String(s.norm_id || '').trim(),
          title: String(s.title || '').trim() || undefined,
          fragment_text: String(s.fragment_text || '').trim() || undefined,
          ris_url: String(s.ris_url || '').trim() || undefined,
          valid_from: String(s.valid_from || '').trim() || undefined,
        }))
        .filter((s) => s.norm_id)
    : undefined;
  const normChainRaw = Array.isArray(o.norm_chain)
    ? o.norm_chain
    : Array.isArray(o.normChain)
      ? o.normChain
      : undefined;
  const norm_chain = normChainRaw
    ? normChainRaw
        .filter((e): e is Record<string, unknown> => Boolean(e && typeof e === 'object'))
        .map((e) => {
          const rankRaw = e.rank;
          const rank =
            typeof rankRaw === 'number'
              ? rankRaw
              : typeof rankRaw === 'string' && rankRaw.trim()
                ? Number(rankRaw)
                : undefined;
          return {
            norm_id: String(e.norm_id || e.normId || '').trim(),
            rank: typeof rank === 'number' && Number.isFinite(rank) ? rank : undefined,
            via: String(e.via || '').trim() || undefined,
          };
        })
        .filter((e) => e.norm_id)
        .slice(0, 24)
    : undefined;
  const proof: LexProof = {
    audit_id: String(o.audit_id || '').trim() || undefined,
    snapshot_version: String(o.snapshot_version || '').trim() || undefined,
    lex_stamp: String(o.lex_stamp || '').trim() || undefined,
    retrieved_at: String(o.retrieved_at || '').trim() || undefined,
    query_reason: String(o.query_reason || '').trim() || undefined,
    query_enriched: o.query_enriched === true,
    lex_query: String(o.lex_query || '').trim() || undefined,
    lex_diag: String(o.lex_diag || '').trim() || undefined,
    quality_gate: String(o.quality_gate || '').trim() || undefined,
    sources: sources && sources.length > 0 ? sources : undefined,
    norm_chain: norm_chain && norm_chain.length > 0 ? norm_chain : undefined,
  };
  const hasData =
    proof.lex_stamp ||
    proof.audit_id ||
    proof.snapshot_version ||
    (proof.sources && proof.sources.length > 0) ||
    (proof.norm_chain && proof.norm_chain.length > 0) ||
    proof.lex_diag ||
    proof.query_reason;
  return hasData ? proof : null;
}

export function hasLexProofData(proof?: LexProof | null): boolean {
  return Boolean(proof && parseLexProof(proof));
}

/**
 * Diagnose-Werte, bei denen LEXCORE *keine* verifizierte Norm geliefert hat
 * (ehrliche Absage / Fehler / verworfene Antwort) — kein „verifiziert"-Badge.
 */
const LEX_DIAG_UNVERIFIED = new Set<string>([
  'weak_match',
  'no_sources',
  'http_error',
  'replaced_no_lexcore_stamp',
  'replaced_contradiction_with_stamp',
  'rejected_hallucinated_norm_id',
  'vague_no_match',
  'no_sources_honest',
  'no_match_above_threshold',
  'replaced_no_stamp_doc_aware',
  'replaced_contradiction_doc_aware',
  'allrounder_fallback_doc_aware',
  'allrounder_fallback',
  'kuendigung_vs_mangel',
]);

/**
 * Strukturierter Nachweis = verifizierte LEXCORE-Antwort?
 * True nur mit gültigem LEX-Stempel und ohne Absage-/Schwellen-Diagnose.
 * Quellen unter der Trefferschwelle sind Kandidaten — kein grünes Badge.
 */
export function isVerifiedLexProof(proof?: LexProof | null): boolean {
  const p = parseLexProof(proof ?? null);
  if (!p) return false;
  if (p.lex_diag && LEX_DIAG_UNVERIFIED.has(p.lex_diag)) return false;
  if (p.quality_gate && LEX_DIAG_UNVERIFIED.has(p.quality_gate)) return false;
  return Boolean(p.lex_stamp);
}

/**
 * Sichtbarer LEX-Block aus Sources (nicht aus Modell-Text).
 * Kein Fake: nur mit fragment_text und ohne Absage-/Miss-Diagnose.
 * Stempel darf während des Streams noch fehlen (Event lex_sources).
 */
export function hasLexQuoteBlock(proof?: LexProof | null): boolean {
  const p = parseLexProof(proof ?? null);
  if (!p) return false;
  if (p.lex_diag && LEX_DIAG_UNVERIFIED.has(p.lex_diag)) return false;
  if (p.quality_gate && LEX_DIAG_UNVERIFIED.has(p.quality_gate)) return false;
  return Boolean(p.sources?.some((s) => s.norm_id && s.fragment_text));
}

/** Erste klickbare RIS-URL aus dem strukturierten Nachweis (für das Vertrauens-Badge). */
export function firstRisUrlFromProof(proof?: LexProof | null): string | undefined {
  const p = parseLexProof(proof ?? null);
  const hit = p?.sources?.find((s) => s.ris_url && s.ris_url.startsWith('http'));
  return hit?.ris_url;
}

export function explainLexProof(proof: LexProof): string {
  const parts: string[] = [];
  if (proof.lex_diag && LEX_DIAG_LABELS[proof.lex_diag]) {
    parts.push(LEX_DIAG_LABELS[proof.lex_diag]);
  } else if (proof.quality_gate && LEX_DIAG_LABELS[proof.quality_gate]) {
    parts.push(LEX_DIAG_LABELS[proof.quality_gate]);
  }
  if (proof.query_reason && QUERY_REASON_LABELS[proof.query_reason]) {
    parts.push(QUERY_REASON_LABELS[proof.query_reason]);
  } else if (proof.query_enriched) {
    parts.push('Die Suchanfrage wurde mit Inhalten aus dem geöffneten Dokument angereichert.');
  }
  if (parts.length === 0) {
    parts.push('Antwort basiert auf dem belegten Normenstand dieser Anfrage.');
  }
  return parts.join(' ');
}

export function formatLexRetrievedAt(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 19);
    return d.toLocaleString('de-AT', {
      timeZone: 'Europe/Vienna',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso.slice(0, 19);
  }
}

/** LEX-Stempel am Ende des Fließtexts ausblenden — Details stehen im Ausklapp-Block. */
export function stripLexStampForDisplay(text: string): string {
  return text
    .replace(/\n*\[LEX:[^\]]+\]\s*$/i, '')
    .replace(/^Hier der verifizierte Wortlaut:\s*\n+/i, '')
    .trim();
}

const LEX_REFUSAL_RE =
  /keine verifizierte norm|keine einzelne norm verifiziert|noch keine verifizierte auskunft|oberhalb der trefferschwelle|verworfen|nicht einschlaegig|nicht einschlägig|keine passende norm/i;

/** Ehrliche LEXCORE-Absage im Antworttext (keine verifizierte Norm)? */
export function isLexRefusal(text: string): boolean {
  return LEX_REFUSAL_RE.test(text);
}

/**
 * Bubble-Text bei LEXCORE: substantieller Fließtext mit wörtlichem §-Bezug und
 * inline [NORM-ID]. Nur der maschinelle [LEX:…]-Stempel (und das interne
 * "Hier der verifizierte Wortlaut:"-Präfix) wandert in den Ausklapp-Nachweis;
 * der eigentliche Antworttext bleibt vollständig im Bubble.
 *
 * Früher (bis UAT 28.06.2026) wurde der gesamte Renderer-Fließtext bei
 * vorhandenen Norm-IDs durch eine Einzeiler-Zusammenfassung
 * ("Verifizierte Normen (…) — Details im Nachweis (LEX) unten.") ersetzt — der
 * vom n8n-Renderer erzeugte Wortlaut samt inline [NORM-ID] ging dadurch im Chat
 * verloren und stand nur noch im LEX-Panel. Andreas-Follow-up Paket 2: der
 * Wortlaut gehört in den Fließtext, das Panel bleibt der zusätzliche Audit-Nachweis.
 *
 * Der `normIds`-Parameter bleibt aus Signatur-Kompatibilität erhalten.
 */
export function formatBotMessageForDisplay(
  text: string,
  lexProof?: LexProof | null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  normIds?: string[],
): string {
  const hasLex =
    hasLexProofData(lexProof) || /\[[A-Za-z][A-Za-z-]{1,11}-\d+/i.test(text) || /\[LEX:/i.test(text);

  if (!hasLex) return text;

  // Verifizierte Antwort, Hard-Quote ODER ehrliche Absage: vollständiger
  // Fließtext im Bubble; nur Stempel/Präfix werden für die Anzeige entfernt.
  return stripLexStampForDisplay(text);
}
